import type { FC, ReactNode } from "react"

import { useActiveOrganization, useAuthentication } from "@/contexts"
import { useFlags } from "@/services/feature"
import type { FeatureFlagName } from "@/services/feature"

import { Page404 } from "../../nav/page404"

interface DataManagerGuardProps {
  /**
   * The children to render if access is granted
   */
  children: ReactNode
  /**
   * The feature flag to check for non-admin/org_admin users
   */
  feature: FeatureFlagName
}

/**
 * @description A guard specifically for data manager features.
 * Admin and org_admin users always have access regardless of feature flags.
 * Other users need the feature flag to be enabled.
 */
export const DataManagerGuard: FC<DataManagerGuardProps> = ({
  children,
  feature,
}) => {
  const { activeOrganization } = useActiveOrganization()
  const { isAdminWriterUser, isOrgCredentialsAdminUser, isOrgAdminUser } =
    useAuthentication()
  const { flagsIsFetched, ...flags } = useFlags()

  if (!activeOrganization) {
    return null
  }

  // Admin and org_admin always have access to data manager features
  if (isAdminWriterUser || isOrgAdminUser) {
    return children
  }

  // org_utility_credentials_admin is explicitly denied from Data Manager.
  if (isOrgCredentialsAdminUser) {
    return <Page404 />
  }

  if (!flagsIsFetched) {
    return null
  }

  const isFeatureEnabled = flags[feature]

  if (isFeatureEnabled) {
    return children
  }

  return <Page404 />
}
